import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ChevronLeft, ChevronRight, Upload, X, FileCheck, Loader2 } from "lucide-react";
import LeadCaptureCTA from "@/components/LeadCaptureCTA";

const STEPS = [
  "Customer",
  "Vehicle",
  "Insurance",
  "Accident",
  "Documents",
  "Review",
];

const INSURERS = [
  "ICICI Lombard", "HDFC ERGO", "Tata AIG", "Bajaj Allianz", "Reliance General",
  "SBI General", "Digit Insurance", "New India Assurance", "Oriental Insurance",
  "National Insurance", "United India", "Acko General", "Chola MS", "Royal Sundaram",
];

const DOC_CATEGORIES = [
  "RC", "Driving License", "Insurance Policy", "FIR", "Accident Photo", "Repair Estimate", "Aadhaar", "Other",
];

export default function FileClaim() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docs, setDocs] = useState([]); // {doc_id, original_filename, category}
  const [data, setData] = useState({
    customer: { name: user?.name || "", mobile: "", email: user?.email || "", address: "", city: "", pincode: "" },
    vehicle: { reg_no: "", make: "", model: "", year: new Date().getFullYear(), fuel_type: "Petrol", chassis_no: "", engine_no: "" },
    insurance: { insurer: "", policy_no: "", policy_type: "Comprehensive", valid_till: "" },
    accident: { date: new Date().toISOString().slice(0, 10), time: "", location: "", description: "", fir_no: "", third_party_involved: false, injuries: false },
  });

  const set = (section, key, value) =>
    setData((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));

  const validateStep = () => {
    if (step === 0) {
      const c = data.customer;
      return c.name && c.mobile && c.mobile.length >= 10;
    }
    if (step === 1) {
      const v = data.vehicle;
      return v.reg_no && v.make && v.model && v.year;
    }
    if (step === 2) {
      const i = data.insurance;
      return i.insurer && i.policy_no;
    }
    if (step === 3) {
      const a = data.accident;
      return a.date && a.location && a.description;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleUpload = async (file, category) => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      const { data: res } = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((d) => [...d, { doc_id: res.doc_id, original_filename: file.name, category }]);
    } catch (e) {
      alert("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingDoc(false);
    }
  };

  const removeDoc = (id) => setDocs((d) => d.filter((x) => x.doc_id !== id));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...data, document_ids: docs.map((d) => d.doc_id) };
      const { data: res } = await api.post("/claims", payload);
      navigate(`/claim/${res.claim_id}`, { state: { justCreated: true } });
    } catch (e) {
      alert("Failed to file claim. Please sign in first.");
      if (e.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] py-10" data-testid="file-claim-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] mb-2">Claim Registration</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            File a new motor claim
          </h1>
          <p className="text-slate-600 mt-1">Takes ~3 minutes. We'll save your progress between steps.</p>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 overflow-x-auto" data-testid="claim-stepper">
          <div className="flex items-center gap-2 min-w-max">
            {STEPS.map((label, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <div key={label} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      done ? "bg-[#00A86B] text-white"
                        : active ? "bg-[#003DA5] text-white"
                          : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-xs font-semibold ${active ? "text-[#003DA5]" : "text-slate-600"} hidden sm:inline`}>{label}</span>
                  {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${done ? "bg-[#00A86B]" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          {step === 0 && (
            <>
              <LeadCaptureCTA source="claim_wizard_step1" />
              <CustomerStep data={data.customer} set={(k, v) => set("customer", k, v)} />
            </>
          )}
          {step === 1 && <VehicleStep data={data.vehicle} set={(k, v) => set("vehicle", k, v)} />}
          {step === 2 && <InsuranceStep data={data.insurance} set={(k, v) => set("insurance", k, v)} />}
          {step === 3 && <AccidentStep data={data.accident} set={(k, v) => set("accident", k, v)} />}
          {step === 4 && (
            <DocumentsStep
              docs={docs}
              onUpload={handleUpload}
              onRemove={removeDoc}
              uploading={uploadingDoc}
              loggedIn={!!user}
            />
          )}
          {step === 5 && <ReviewStep data={data} docs={docs} />}

          <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
            <button
              onClick={back}
              disabled={step === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-700 font-semibold disabled:opacity-40 hover:bg-slate-100"
              data-testid="claim-back"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                disabled={!validateStep()}
                className="inline-flex items-center gap-2 bg-[#003DA5] hover:bg-[#002F80] disabled:bg-slate-300 text-white rounded-xl px-5 py-2.5 font-semibold transition-colors"
                data-testid="claim-next"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#FF7A00] hover:bg-[#CC6200] disabled:bg-slate-300 text-white rounded-xl px-5 py-2.5 font-semibold transition-colors"
                data-testid="claim-submit"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit Claim <ChevronRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>

        {!user && (
          <p className="text-center text-xs text-slate-500 mt-4">
            You'll be asked to sign in with Google before submitting.
          </p>
        )}
      </div>
    </div>
  );
}

// ----- Step components -----
function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">
        {label} {required && <span className="text-[#FF7A00]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = "w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-[#003DA5] focus:border-transparent outline-none transition-shadow";

function CustomerStep({ data, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Full Name" required><input data-testid="cust-name" className={inputCls} value={data.name} onChange={(e) => set("name", e.target.value)} /></Field>
      <Field label="Mobile" required><input data-testid="cust-mobile" className={inputCls} value={data.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} maxLength={10} placeholder="10-digit mobile" /></Field>
      <Field label="Email"><input data-testid="cust-email" className={inputCls} value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></Field>
      <Field label="City"><input data-testid="cust-city" className={inputCls} value={data.city} onChange={(e) => set("city", e.target.value)} /></Field>
      <Field label="Pincode"><input data-testid="cust-pincode" className={inputCls} value={data.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} maxLength={6} /></Field>
      <div className="sm:col-span-2"><Field label="Address"><textarea data-testid="cust-address" rows={2} className={inputCls} value={data.address} onChange={(e) => set("address", e.target.value)} /></Field></div>
    </div>
  );
}

function VehicleStep({ data, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Registration No." required><input data-testid="veh-reg" className={inputCls} value={data.reg_no} onChange={(e) => set("reg_no", e.target.value.toUpperCase())} placeholder="DL01AB1234" /></Field>
      <Field label="Year of Manufacture" required>
        <input data-testid="veh-year" type="number" min="1990" max={new Date().getFullYear() + 1} className={inputCls} value={data.year} onChange={(e) => set("year", parseInt(e.target.value || 0))} />
      </Field>
      <Field label="Make" required><input data-testid="veh-make" className={inputCls} value={data.make} onChange={(e) => set("make", e.target.value)} placeholder="Maruti, Hyundai…" /></Field>
      <Field label="Model" required><input data-testid="veh-model" className={inputCls} value={data.model} onChange={(e) => set("model", e.target.value)} placeholder="Swift, Creta…" /></Field>
      <Field label="Fuel Type">
        <select data-testid="veh-fuel" className={inputCls} value={data.fuel_type} onChange={(e) => set("fuel_type", e.target.value)}>
          {["Petrol", "Diesel", "CNG", "Electric", "Hybrid"].map((f) => <option key={f}>{f}</option>)}
        </select>
      </Field>
      <Field label="Chassis No."><input data-testid="veh-chassis" className={inputCls} value={data.chassis_no} onChange={(e) => set("chassis_no", e.target.value)} /></Field>
      <Field label="Engine No."><input data-testid="veh-engine" className={inputCls} value={data.engine_no} onChange={(e) => set("engine_no", e.target.value)} /></Field>
    </div>
  );
}

function InsuranceStep({ data, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Insurance Company" required>
        <select data-testid="ins-insurer" className={inputCls} value={data.insurer} onChange={(e) => set("insurer", e.target.value)}>
          <option value="">Select…</option>
          {INSURERS.map((i) => <option key={i}>{i}</option>)}
        </select>
      </Field>
      <Field label="Policy Number" required><input data-testid="ins-policy" className={inputCls} value={data.policy_no} onChange={(e) => set("policy_no", e.target.value)} /></Field>
      <Field label="Policy Type">
        <select data-testid="ins-type" className={inputCls} value={data.policy_type} onChange={(e) => set("policy_type", e.target.value)}>
          {["Comprehensive", "Third Party", "Standalone OD", "Bundled"].map((t) => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Valid Till"><input data-testid="ins-valid" type="date" className={inputCls} value={data.valid_till} onChange={(e) => set("valid_till", e.target.value)} /></Field>
    </div>
  );
}

function AccidentStep({ data, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Accident Date" required><input data-testid="acc-date" type="date" className={inputCls} value={data.date} onChange={(e) => set("date", e.target.value)} /></Field>
      <Field label="Time"><input data-testid="acc-time" type="time" className={inputCls} value={data.time} onChange={(e) => set("time", e.target.value)} /></Field>
      <div className="sm:col-span-2"><Field label="Location" required><input data-testid="acc-location" className={inputCls} value={data.location} onChange={(e) => set("location", e.target.value)} placeholder="Nearest landmark, city" /></Field></div>
      <div className="sm:col-span-2"><Field label="What happened?" required><textarea data-testid="acc-description" rows={3} className={inputCls} value={data.description} onChange={(e) => set("description", e.target.value)} placeholder="Briefly describe the accident…" /></Field></div>
      <Field label="FIR No. (if any)"><input data-testid="acc-fir" className={inputCls} value={data.fir_no} onChange={(e) => set("fir_no", e.target.value)} /></Field>
      <div className="flex flex-col gap-2 pt-2">
        <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="acc-third-party" type="checkbox" checked={data.third_party_involved} onChange={(e) => set("third_party_involved", e.target.checked)} /> Third-party involved</label>
        <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="acc-injuries" type="checkbox" checked={data.injuries} onChange={(e) => set("injuries", e.target.checked)} /> Any injuries</label>
      </div>
    </div>
  );
}

function DocumentsStep({ docs, onUpload, onRemove, uploading, loggedIn }) {
  const [category, setCategory] = useState(DOC_CATEGORIES[0]);
  if (!loggedIn) {
    return (
      <div className="text-center py-10">
        <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="font-semibold text-slate-800">Sign in to upload documents</p>
        <p className="text-sm text-slate-500 mt-1">Your document vault is encrypted and tied to your account.</p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-sm text-slate-600 mb-4">Upload the documents you have — RC, DL, policy copy, accident photos, repair estimate. You can always add more later.</p>
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-6 text-center">
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center mb-3">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls + " sm:w-48"} data-testid="doc-category">
            {DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 bg-[#003DA5] hover:bg-[#002F80] text-white rounded-xl px-5 py-2.5 font-semibold cursor-pointer" data-testid="doc-upload-button">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose File"}
            <input type="file" className="hidden" onChange={(e) => onUpload(e.target.files?.[0], category)} disabled={uploading} />
          </label>
        </div>
        <p className="text-xs text-slate-500">JPG, PNG, PDF up to 10MB</p>
      </div>

      {docs.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="uploaded-docs-list">
          {docs.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileCheck className="w-5 h-5 text-[#00A86B] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{d.original_filename}</div>
                  <div className="text-xs text-slate-500">{d.category}</div>
                </div>
              </div>
              <button onClick={() => onRemove(d.doc_id)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400" data-testid={`remove-doc-${d.doc_id}`}>
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReviewStep({ data, docs }) {
  const sections = [
    { title: "Customer", data: data.customer },
    { title: "Vehicle", data: data.vehicle },
    { title: "Insurance", data: data.insurance },
    { title: "Accident", data: data.accident },
  ];
  return (
    <div data-testid="claim-review">
      <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">Review your claim</h3>
      <p className="text-sm text-slate-500 mb-5">Once submitted, you'll get a Claim ID and your Claim Manager will reach out within 30 minutes.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {sections.map((s) => (
          <div key={s.title} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{s.title}</div>
            <dl className="text-sm space-y-1">
              {Object.entries(s.data).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-slate-900 font-medium text-right truncate">{typeof v === "boolean" ? (v ? "Yes" : "No") : (v || "—")}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 sm:col-span-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Documents ({docs.length})</div>
          {docs.length === 0 ? (
            <p className="text-sm text-slate-500">No documents uploaded yet. You can add them later from your dashboard.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {docs.map((d) => (
                <li key={d.doc_id} className="flex items-center gap-2"><FileCheck className="w-4 h-4 text-[#00A86B]" /> {d.original_filename} <span className="text-slate-400">— {d.category}</span></li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
